// MagicLinkVerify (NEW DESIGN — visual revamp only). Logic verbatim copy of
// MagicLinkVerify.js. Differences:
//   - LoginNewWrapper instead of LoginWrapper.
//   - Navigations use canonical /login (the new design now lives at /login).
//   - Reuses the right-card shell from Login-new (logo + status block + footer).
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { Row, Col, notification } from 'antd';
import { MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { ThemeButton } from '../Styles/Theme';
import { LoginNewWrapper } from './LoginNewStyle';
import LoginLeftPanel from './LoginLeftPanel';
import { resendMagicLinkApi } from '../../store/actions/loginAction';
import { isLogin } from '../../utils/helpers';

const MagicLinkVerifyNew = (props) => {
	const { resend } = props;
	const navigate = useNavigate();
	const [resending, setResending] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [mfaSessionToken, setMfaSessionToken] = useState(null);
	const [maskedEmail, setMaskedEmail] = useState('');
	const cooldownTimerRef = useRef(null);

	useEffect(() => {
		if (isLogin()) {
			navigate('/', { replace: true });
			return;
		}
		const token = localStorage.getItem('mfaSessionToken');
		if (!token) {
			notification.warning({ message: 'Please sign in again.' });
			navigate('/login', { replace: true });
			return;
		}
		setMfaSessionToken(token);
		setMaskedEmail(localStorage.getItem('mfaMaskedEmail') || '');
	}, [navigate]);

	useEffect(() => {
		return () => {
			if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
		};
	}, []);

	const startCooldown = (seconds) => {
		setCooldown(seconds);
		if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
		cooldownTimerRef.current = setInterval(() => {
			setCooldown((prev) => {
				if (prev <= 1) {
					clearInterval(cooldownTimerRef.current);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	};

	const kickToLogin = (msg) => {
		localStorage.removeItem('mfaSessionToken');
		localStorage.removeItem('mfaMaskedEmail');
		localStorage.removeItem('mfaExpiryMinutes');
		if (msg) {
			notification.warning({ message: 'Please sign in again', description: msg });
		}
		navigate('/login', { replace: true });
	};

	const handleResend = async () => {
		if (!mfaSessionToken || cooldown > 0 || resending) return;
		setResending(true);
		const result = await resend({ mfaSessionToken });
		setResending(false);
		if (result?.status === 1) {
			notification.success({
				message: 'A new sign-in link has been sent to your email.',
				description: 'Always use the link from the most recent email - older ones no longer work.',
			});
			startCooldown(30);
			return;
		}

		const code = result?.data?.code;
		if (code === 'MFA_RESEND_COOLDOWN') {
			const retry = result?.data?.retryAfterSeconds || 30;
			notification.warning({
				message: 'Please wait',
				description: `You can request another link in ${retry}s.`,
			});
			startCooldown(retry);
		} else if (code === 'MFA_MAX_RESENDS') {
			kickToLogin("You've reached the resend limit for this attempt. Please start a fresh sign-in.");
		} else if (code === 'MFA_SESSION_INVALID' || code === 'MFA_LINK_EXPIRED') {
			kickToLogin('This sign-in session has expired. Please start a fresh sign-in.');
		} else {
			notification.error({
				message: 'Could not resend the link',
				description: 'Please try again in a moment, or go back and sign in again.',
			});
		}
	};

	const handleRefresh = () => {
		window.location.reload();
	};

	const handleBackToLogin = () => {
		localStorage.removeItem('mfaSessionToken');
		localStorage.removeItem('mfaMaskedEmail');
		localStorage.removeItem('mfaExpiryMinutes');
		navigate('/login', { replace: true });
	};

	return (
		<LoginNewWrapper>
			<Row gutter={[20, 16]}>
				<Col
					xs={{ span: 24, order: 1 }}
					sm={{ span: 24, order: 1 }}
					md={{ span: 12, order: 2 }}
					lg={{ span: 14, order: 2 }}
					xl={{ span: 14, order: 2 }}
				>
					<div className='loginRightCard'>
						<div className='loginLogo'>
							<img src='/images/login-new/crowdvox-logo.svg' alt='CrowdVox' />
						</div>

						<div className='loginContainer'>
							<div className='loginStatusBlock'>
								<div className='loginStatusIcon'>
									<MailOutlined />
								</div>
								<h1>Check your email</h1>
								<p>
									We sent a sign-in link to <strong>{maskedEmail || 'your email'}</strong>.
									<br />
									Click the link to sign in.
								</p>

								<div className='loginInfoCard'>
									<strong>Important:</strong> Open the link in <strong>this same browser</strong>.
									After you click the link and see a success page, come back here and click{' '}
									<strong>Refresh</strong> - you&rsquo;ll be signed in automatically. If you hit{' '}
									<strong>Resend link</strong>, older emails stop working - always use the most
									recent one.
								</div>

								<ThemeButton
									size='large'
									className='loginBtn'
									icon={<ReloadOutlined />}
									onClick={handleRefresh}
									style={{ width: '100%', marginBottom: 14 }}
								>
									Refresh
								</ThemeButton>

								<div style={{ marginTop: 4 }}>
									<span style={{ color: '#6D6A67', fontSize: 13 }}>
										Didn&rsquo;t receive the email?{' '}
									</span>
									<a
										onClick={handleResend}
										className={`loginLink${cooldown > 0 || resending ? ' is-disabled' : ''}`}
										style={{ fontSize: 13 }}
									>
										{resending
											? 'Sending...'
											: cooldown > 0
												? `Resend in ${cooldown}s`
												: 'Resend link'}
									</a>
								</div>

								<div style={{ marginTop: 12 }}>
									<a className='loginMutedLink' onClick={handleBackToLogin}>
										Back to sign in
									</a>
								</div>
							</div>
						</div>

						<div className='loginFooter'>
							<span>@{new Date().getFullYear()} CrowdVox Inc.</span>
						</div>
					</div>
				</Col>

				<Col
					xs={{ span: 0, order: 2 }}
					sm={{ span: 0, order: 2 }}
					md={{ span: 12, order: 1 }}
					lg={{ span: 10, order: 1 }}
					xl={{ span: 10, order: 1 }}
				>
					<LoginLeftPanel />
				</Col>
			</Row>
		</LoginNewWrapper>
	);
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
	resend: (params) => dispatch(resendMagicLinkApi(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MagicLinkVerifyNew);
